import { Alert } from "@mui/material";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import StripeCheckoutSessionPaymentElement, {
  StripeCardSubmissionState,
} from "./StripeCheckoutSessionPaymentElement";
import {
  buildStaffErrorFallback,
  getUserFacingErrorText,
  STAFF_SUPPORT_EMAIL,
} from "../../../utils/userFacingError";

// Steps after the card form could not open. No card was charged.
const CARD_FORM_RETRY_STEPS = `Close Process Payment and open it again. If it keeps happening, email ${STAFF_SUPPORT_EMAIL}.`;
const CARD_FORM_NOT_OPENED_TEXT = `The card form could not open, so no payment was taken. ${CARD_FORM_RETRY_STEPS}`;

// The payment went through; only the follow-up view failed.
const PAYMENT_RESULT_NOT_SHOWN_TEXT =
  "The card payment was received, but we could not show the result. Refresh the page to see the payment. Do not take the payment again.";
const PAYMENT_STATUS_REFRESH_FALLBACK = buildStaffErrorFallback(
  "refresh the payment status",
  "The status shown has not changed.",
);

export type SecureCardCheckoutMode = "CUSTOM" | "HOSTED_FALLBACK";

export type SecureCardCheckoutStatus =
  | "PENDING"
  | "PAID"
  | "EXPIRED"
  | "RECONCILIATION_REQUIRED";

export type SecureCardCheckoutHandoffData = {
  amountInCents: number;
  checkoutMode: SecureCardCheckoutMode;
  checkoutSessionId: string;
  checkoutUrl?: string | null;
  clientSecret?: string | null;
  connectedAccountId: string;
  expiresAtUtcMilli: number;
  publishableKey: string;
};

export type SecureCardCheckoutHandoffProps = SecureCardCheckoutHandoffData & {
  /** Shown above Confirm & Record Payment (Internal Notes, design). */
  beforeSubmit?: React.ReactNode;
  /** Runs before the embedded card form confirms this Session. */
  onBeforeConfirm?: (checkoutSessionId: string) => Promise<void>;
  onPaymentProcessed?: () => Promise<void> | void;
  onPaymentSubmitted: () => Promise<void> | void;
  /**
   * True from card submit until the invoice shows the payment, Stripe returns
   * an error, or automatic status checks stop. The popup locks close, amount
   * and method while it is true (UX audit INV-1).
   */
  onPaymentInProgressChange?: (inProgress: boolean) => void;
  onRefreshStatus: () => Promise<void> | void;
  onRequestHostedFallback: (
    customCheckoutSessionId: string,
  ) => Promise<SecureCardCheckoutHandoffData | null>;
  refreshingStatus?: boolean;
  requestingHostedFallback?: boolean;
  returnUrl: string;
  status: SecureCardCheckoutStatus;
  statusRefreshError?: string | null;
};

export const secureCardCheckoutPollIntervalUtcMilli = 5_000;
export const secureCardCheckoutMaximumAutomaticPolls = 24;

export const isSecureCardPaymentInProgress = ({
  automaticPollingPaused,
  effectiveStatus,
  submission,
}: {
  automaticPollingPaused: boolean;
  effectiveStatus: SecureCardCheckoutStatus;
  submission: StripeCardSubmissionState;
}) =>
  submission === "SUBMITTING" ||
  // After Stripe accepts the card, the lock holds until the invoice readback
  // leaves PENDING. When automatic checks stop, staff can close the popup and
  // reopen it; the backend refuses a new checkout while this one may be paid.
  (submission === "SUBMITTED" &&
    effectiveStatus === "PENDING" &&
    !automaticPollingPaused);

const secondInUtcMilli = 1_000;

const expiryFormatter = new Intl.DateTimeFormat(undefined, {
  dateStyle: "medium",
  timeStyle: "short",
});

// The Manage Invoices page still validates legacy hosted Session responses.
// This popup never opens or links to them. The approved payment experience is
// the embedded Stripe Payment Element only.
export const isSafeHostedCheckoutUrl = (checkoutUrl?: string | null) => {
  if (!checkoutUrl) {
    return false;
  }

  try {
    const parsedUrl = new URL(checkoutUrl);

    return (
      parsedUrl.protocol === "https:" &&
      parsedUrl.hostname === "checkout.stripe.com" &&
      parsedUrl.port === "" &&
      parsedUrl.username === "" &&
      parsedUrl.password === "" &&
      parsedUrl.pathname !== "/"
    );
  } catch {
    return false;
  }
};

export const getEffectiveSecureCardCheckoutStatus = ({
  expiresAtUtcMilli,
  nowUtcMilli,
  status,
}: {
  expiresAtUtcMilli: number;
  nowUtcMilli: number;
  status: SecureCardCheckoutStatus;
}): SecureCardCheckoutStatus => {
  if (status === "PAID" || status === "RECONCILIATION_REQUIRED") {
    return status;
  }

  if (
    status === "EXPIRED" ||
    !Number.isFinite(expiresAtUtcMilli) ||
    expiresAtUtcMilli <= nowUtcMilli
  ) {
    return "EXPIRED";
  }

  return "PENDING";
};

const formatRemainingTime = ({
  expiresAtUtcMilli,
  nowUtcMilli,
}: {
  expiresAtUtcMilli: number;
  nowUtcMilli: number;
}) => {
  const remainingSeconds = Math.max(
    0,
    Math.ceil((expiresAtUtcMilli - nowUtcMilli) / secondInUtcMilli),
  );
  const minutes = Math.floor(remainingSeconds / 60);
  const seconds = remainingSeconds % 60;

  if (minutes >= 60) {
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;

    return `${hours}h ${remainingMinutes}m`;
  }

  return `${minutes}m ${String(seconds).padStart(2, "0")}s`;
};

const getStatusCopy = (status: SecureCardCheckoutStatus) => {
  if (status === "PAID") {
    return {
      alertSeverity: "success" as const,
      detail: "The payment is now recorded on this invoice.",
      label: "Payment confirmed",
    };
  }

  if (status === "RECONCILIATION_REQUIRED") {
    return {
      alertSeverity: "error" as const,
      detail: `The card may have been charged, but the invoice does not show the payment yet. Do not take the payment again. Email ${STAFF_SUPPORT_EMAIL} so we can check it.`,
      label: "Payment needs a check",
    };
  }

  if (status === "EXPIRED") {
    return {
      alertSeverity: "warning" as const,
      detail:
        "This secure payment session has expired. Close this popup and open Add Payment again.",
      label: "Payment session expired",
    };
  }

  return {
    alertSeverity: "info" as const,
    detail:
      "The invoice changes to Paid only after Stripe confirms the payment.",
    label: "Secure payment ready",
  };
};

const SecureCardCheckoutHandoff = ({
  amountInCents,
  beforeSubmit,
  checkoutMode,
  checkoutSessionId,
  clientSecret,
  connectedAccountId,
  expiresAtUtcMilli,
  onBeforeConfirm,
  onPaymentInProgressChange,
  onPaymentProcessed,
  onPaymentSubmitted,
  onRefreshStatus,
  publishableKey,
  refreshingStatus = false,
  returnUrl,
  status,
  statusRefreshError,
}: SecureCardCheckoutHandoffProps) => {
  const checkout = useMemo<SecureCardCheckoutHandoffData>(
    () => ({
      amountInCents,
      checkoutMode,
      checkoutSessionId,
      clientSecret,
      connectedAccountId,
      expiresAtUtcMilli,
      publishableKey,
    }),
    [
      amountInCents,
      checkoutMode,
      checkoutSessionId,
      clientSecret,
      connectedAccountId,
      expiresAtUtcMilli,
      publishableKey,
    ],
  );
  const [nowUtcMilli, setNowUtcMilli] = useState(() => Date.now());
  const [localActionError, setLocalActionError] = useState<string | null>(null);
  const [paymentElementUnavailable, setPaymentElementUnavailable] = useState<
    string | null
  >(null);
  const [automaticPollingPaused, setAutomaticPollingPaused] = useState(false);
  const [cardSubmission, setCardSubmission] =
    useState<StripeCardSubmissionState>("IDLE");
  const onPaymentInProgressChangeRef = useRef(onPaymentInProgressChange);
  const automaticPollCountRef = useRef(0);
  const refreshInFlightRef = useRef(false);
  const mountedRef = useRef(true);
  const paymentProcessedSessionIdsRef = useRef(new Set<string>());
  const onPaymentProcessedRef = useRef(onPaymentProcessed);
  const onRefreshStatusRef = useRef(onRefreshStatus);

  const amountIsValid =
    Number.isSafeInteger(checkout.amountInCents) && checkout.amountInCents > 0;
  const effectiveStatus = getEffectiveSecureCardCheckoutStatus({
    expiresAtUtcMilli: checkout.expiresAtUtcMilli,
    nowUtcMilli,
    status,
  });
  const statusNeedsAutomaticPolling =
    effectiveStatus === "PENDING" ||
    effectiveStatus === "RECONCILIATION_REQUIRED";
  const statusCopy = getStatusCopy(effectiveStatus);
  const paymentInProgress = isSecureCardPaymentInProgress({
    automaticPollingPaused,
    effectiveStatus,
    submission: cardSubmission,
  });

  useEffect(() => {
    onPaymentInProgressChangeRef.current = onPaymentInProgressChange;
  }, [onPaymentInProgressChange]);

  useEffect(() => {
    onPaymentInProgressChangeRef.current?.(paymentInProgress);
  }, [paymentInProgress]);

  // An unmounted form can no longer hold the popup open.
  useEffect(
    () => () => {
      onPaymentInProgressChangeRef.current?.(false);
    },
    [],
  );

  useEffect(() => {
    onRefreshStatusRef.current = onRefreshStatus;
  }, [onRefreshStatus]);

  useEffect(() => {
    onPaymentProcessedRef.current = onPaymentProcessed;
  }, [onPaymentProcessed]);

  useEffect(() => {
    mountedRef.current = true;

    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    automaticPollCountRef.current = 0;
    setAutomaticPollingPaused(false);
    setCardSubmission("IDLE");
    setLocalActionError(null);
    setPaymentElementUnavailable(null);
    setNowUtcMilli(Date.now());
  }, [checkout]);

  useEffect(() => {
    const processedSessionId = checkout.checkoutSessionId.trim();

    if (
      effectiveStatus !== "PAID" ||
      !processedSessionId ||
      !onPaymentProcessedRef.current ||
      paymentProcessedSessionIdsRef.current.has(processedSessionId)
    ) {
      return;
    }

    paymentProcessedSessionIdsRef.current.add(processedSessionId);
    Promise.resolve()
      .then(() => onPaymentProcessedRef.current?.())
      .catch((error: unknown) => {
        if (mountedRef.current) {
          setLocalActionError(
            getUserFacingErrorText(error, PAYMENT_RESULT_NOT_SHOWN_TEXT),
          );
        }
      });
  }, [checkout.checkoutSessionId, effectiveStatus]);

  useEffect(() => {
    if (effectiveStatus !== "PENDING") {
      return undefined;
    }

    const intervalId = window.setInterval(() => {
      setNowUtcMilli(Date.now());
    }, secondInUtcMilli);

    return () => window.clearInterval(intervalId);
  }, [effectiveStatus]);

  const handleRefreshStatus = useCallback(async () => {
    if (refreshInFlightRef.current || refreshingStatus) {
      return;
    }

    refreshInFlightRef.current = true;
    if (mountedRef.current) {
      setLocalActionError(null);
    }

    try {
      await onRefreshStatusRef.current();
    } catch (error) {
      if (mountedRef.current) {
        setLocalActionError(
          getUserFacingErrorText(error, PAYMENT_STATUS_REFRESH_FALLBACK),
        );
      }
    } finally {
      refreshInFlightRef.current = false;
    }
  }, [refreshingStatus]);

  useEffect(() => {
    if (!statusNeedsAutomaticPolling || automaticPollingPaused) {
      return undefined;
    }

    const intervalId = window.setInterval(() => {
      if (Date.now() >= checkout.expiresAtUtcMilli) {
        return;
      }

      if (
        automaticPollCountRef.current >= secureCardCheckoutMaximumAutomaticPolls
      ) {
        window.clearInterval(intervalId);
        setAutomaticPollingPaused(true);
        return;
      }

      automaticPollCountRef.current += 1;
      void handleRefreshStatus();
    }, secureCardCheckoutPollIntervalUtcMilli);

    return () => window.clearInterval(intervalId);
  }, [
    automaticPollingPaused,
    checkout.expiresAtUtcMilli,
    handleRefreshStatus,
    statusNeedsAutomaticPolling,
  ]);

  useEffect(() => {
    if (
      status !== "PENDING" ||
      !Number.isFinite(checkout.expiresAtUtcMilli) ||
      checkout.expiresAtUtcMilli <= Date.now()
    ) {
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      setNowUtcMilli(Date.now());
      void handleRefreshStatus();
    }, checkout.expiresAtUtcMilli - Date.now());

    return () => window.clearTimeout(timeoutId);
  }, [checkout.expiresAtUtcMilli, handleRefreshStatus, status]);

  return (
    <section
      aria-labelledby="secure-card-checkout-title"
      className="w-full min-w-0 rounded-[10px] border border-[#D7DBE0] bg-white p-3 sm:p-4"
      data-checkout-mode={checkout.checkoutMode}
      data-checkout-status={effectiveStatus}
      data-testid="secure-card-checkout-handoff"
    >
      <h3
        className="text-[11.5px] font-semibold uppercase leading-4 tracking-[0.05em] text-[#0A0A0A]"
        id="secure-card-checkout-title"
      >
        Card Information
      </h3>
      <p className="mt-1.5 text-[12px] leading-[18px] text-[#4B5563]">
        Enter card details in Stripe's secure form. CMP does not receive or
        store card numbers, expiry dates, or security codes.
      </p>

      <Alert className="mt-3" severity={statusCopy.alertSeverity}>
        <div aria-live="polite" role="status">
          <div className="font-semibold">{statusCopy.label}</div>
          <div className="mt-0.5 text-[12px] leading-[18px]">
            {statusCopy.detail}
          </div>
        </div>
      </Alert>

      {!amountIsValid ? (
        <Alert className="mt-3" severity="error">
          The card payment amount is not correct, so no payment was taken.
          Close Process Payment and open it again.
        </Alert>
      ) : null}

      {localActionError || statusRefreshError ? (
        <Alert className="mt-3" severity="error">
          {localActionError || statusRefreshError}
        </Alert>
      ) : null}

      {automaticPollingPaused && statusNeedsAutomaticPolling ? (
        <Alert className="mt-3" severity="info">
          {effectiveStatus === "RECONCILIATION_REQUIRED"
            ? `We stopped checking for the payment result. Do not take the payment again. Email ${STAFF_SUPPORT_EMAIL} for help.`
            : "We stopped checking for the payment result. Close Process Payment and open it again to see the latest status."}
        </Alert>
      ) : null}

      {effectiveStatus === "PENDING" && amountIsValid ? (
        <div className="mt-3">
          {checkout.checkoutMode !== "CUSTOM" ? (
            <Alert severity="error">{CARD_FORM_NOT_OPENED_TEXT}</Alert>
          ) : checkout.clientSecret ? (
            <StripeCheckoutSessionPaymentElement
              amountInCents={checkout.amountInCents}
              beforeSubmit={beforeSubmit}
              checkoutSessionId={checkout.checkoutSessionId}
              clientSecret={checkout.clientSecret}
              connectedAccountId={checkout.connectedAccountId}
              onBeforeConfirm={
                onBeforeConfirm
                  ? () => onBeforeConfirm(checkout.checkoutSessionId)
                  : undefined
              }
              onPaymentElementUnavailable={setPaymentElementUnavailable}
              onSubmissionStateChange={setCardSubmission}
              onPaymentSubmitted={async () => {
                automaticPollCountRef.current = 0;
                setAutomaticPollingPaused(false);
                await onPaymentSubmitted();
                await handleRefreshStatus();
              }}
              publishableKey={checkout.publishableKey}
              returnUrl={returnUrl}
            />
          ) : (
            <Alert severity="error">{CARD_FORM_NOT_OPENED_TEXT}</Alert>
          )}

          {paymentElementUnavailable ? (
            <Alert className="mt-3" severity="error">
              {paymentElementUnavailable} {CARD_FORM_RETRY_STEPS}
            </Alert>
          ) : null}
        </div>
      ) : null}

      {effectiveStatus === "PENDING" ? (
        <p className="mt-3 text-[11px] leading-4 text-[#6B7280]">
          Session expires{" "}
          {expiryFormatter.format(new Date(checkout.expiresAtUtcMilli))}
          {Number.isFinite(checkout.expiresAtUtcMilli)
            ? ` (${formatRemainingTime({
                expiresAtUtcMilli: checkout.expiresAtUtcMilli,
                nowUtcMilli,
              })} remaining)`
            : ""}
          .
        </p>
      ) : null}

      {/* Without the card form (loading, processing, paid) the slot still shows. */}
      {beforeSubmit && !(effectiveStatus === "PENDING" && amountIsValid &&
        checkout.checkoutMode === "CUSTOM" && checkout.clientSecret) ? (
        <div className="mt-3">{beforeSubmit}</div>
      ) : null}
    </section>
  );
};

export default SecureCardCheckoutHandoff;
